import * as React from 'react';
import { connect } from 'react-redux';
import _ from 'underscore';
import parse from 'html-react-parser'

import FillInBlankLessonForm from './fillInBlankLessonForm';

import {
  FlagDropdown,
  getParameterByName,
  Modal,
  PRODUCTION_FLAG,
} from '../../../Shared/index';
import * as actions from '../../actions/grammarActivities';
import { GrammarActivity } from '../../interfaces/grammarActivities';
import { GrammarActivityState } from '../../reducers/grammarActivitiesReducer';
import { ConceptReducerState } from '../../reducers/conceptsReducer';
import { FillInBlankQuestionsReducerState } from '../../reducers/fillInBlankQuestionsReducer';
import { FillInBlankConceptsFeedbackState } from '../../reducers/fillInBlankConceptsFeedbackReducer';

interface FillInBlankActivitiesProps {
  dispatch: Function;
  lessons: GrammarActivityState;
  fillInBlank: FillInBlankQuestionsReducerState;
  concepts: ConceptReducerState;
  fillInBlankConceptsFeedback: FillInBlankConceptsFeedbackState;
}

interface FillInBlankActivitiesState {
  lessonFlags: string;
}

const ActivityRow = ({ activity, showAllPrompts, fillInBlank, uid, }) => {
  const [showPrompts, setShowPrompts] = React.useState(showAllPrompts)

  React.useEffect(() => {
    setShowPrompts(showAllPrompts)
  }, [showAllPrompts])

  function toggleShowPrompts() { setShowPrompts(!showPrompts) }

  const primaryActivityRow = (
    <tr>
      <td className="activity-title">{activity.title || activity.name || 'No Name'}</td>
      <td>
        <span className="buttons-wrapper">
          <a className="quill-button extra-small green contained focus-on-light" href={`/grammar/#/admin/fitb_activities/${uid}`}>View Activity</a>
          <button className="quill-button extra-small green contained focus-on-light" onClick={toggleShowPrompts} type="button">{showPrompts ? 'Hide' : 'Show'} Prompts</button>
        </span>
      </td>
      <td>{activity.flag}</td>
    </tr>
  )

  if (!showPrompts || !activity.questions) {
    return primaryActivityRow
  }

  const promptRows = activity.questions?.map((q, index) => {
    const question = fillInBlank.data[q.key]

    return (
      <tr className="prompt-row">
        <td colSpan={3}>
          {index + 1}.&nbsp;
          <a href={`/grammar/#/admin/activity-view/${uid}/fill-in-the-blanks/${q.key}/responses`}>{parse(question.prompt)}</a>
          <span className="cues">{question.cues?.map(cue => <span className="cue" key={cue}>{cue}</span>)}</span>
        </td>
      </tr>
    )
  })

  return (
    <React.Fragment>
      {primaryActivityRow}
      {promptRows}
    </React.Fragment>
  )
}

const FillInBlankActivities = ({ dispatch, lessons, fillInBlank, concepts, fillInBlankConceptsFeedback, }: FillInBlankActivitiesProps) => {
  const [lessonFlags, setLessonFlags] = React.useState(getParameterByName('flag') || PRODUCTION_FLAG)
  const [showAllPrompts, setShowAllPrompts] = React.useState(false)

  const createNew = () => { dispatch(actions.toggleNewLessonModal()); }

  const toggleShowAllPrompts = () => { setShowAllPrompts(!showAllPrompts)}

  const handleSelect = (e) => {
    setLessonFlags(e.target.value)
  }

  const renderActivities = () => {
    const { data, hasreceiveddata, } = lessons;
    let keys = _.keys(data);

    // Filter to show only fill-in-the-blank activities
    keys = keys.filter((key: string) => data[key].fill_in_the_blank === true);

    if (lessonFlags !== 'All Flags') {
      keys = keys.filter((key: string) => data[key].flag === lessonFlags)
    }

    const sortedActivityKeys = keys.sort((a, b) => {
      const aTitle = data[a].title || data[a].name
      const bTitle = data[b].title || data[b].name
      if (aTitle && bTitle) {
        return aTitle.localeCompare(bTitle)
      } else {
        return a
      }
    })

    return (
      <table>
        <thead>
          <tr>
            <th>Activity Name</th>
            <th>Actions</th>
            <th>Flag</th>
          </tr>
        </thead>
        <tbody>
          {sortedActivityKeys.map((key) => {
            return (
              <ActivityRow
                activity={data[key]}
                fillInBlank={fillInBlank}
                key={key}
                showAllPrompts={showAllPrompts}
                uid={key}
              />
            )
          })}
        </tbody>
      </table>
    )
  }

  const renderModal = () => {
    const { submittingnew, newLessonModalOpen } = lessons;
    const stateSpecificClass = submittingnew ? 'is-loading' : '';
    if (newLessonModalOpen) {
      return (
        <Modal close={createNew}>
          <FillInBlankLessonForm concepts={concepts} conceptsFeedback={fillInBlankConceptsFeedback} fillInBlank={fillInBlank} stateSpecificClass={stateSpecificClass} submit={submitNewActivity} />
        </Modal>
      );
    }
  }

  const submitNewActivity = (data: GrammarActivity) => {
    // Ensure the activity is marked as fill-in-the-blank
    const activityData = { ...data, fill_in_the_blank: true };
    dispatch(actions.submitNewLesson(activityData));
  }

  return (
    <section className="fill-in-blank-activity-manager">
      <div className="container">
        <h1>Fill in the Blank Activities</h1>
        <button className="quill-button contained green medium focus-on-light" onClick={createNew} type="button">Create New Activity</button>
        {renderModal()}
        <div className="dropdown-and-button-wrapper">
          <FlagDropdown flag={lessonFlags} handleFlagChange={handleSelect} isLessons={true} />
          <button className="quill-button contained green medium focus-on-light" onClick={toggleShowAllPrompts} type="button">{showAllPrompts ? 'Hide' : 'Show'} All Prompts</button>
        </div>
        {renderActivities()}
      </div>
    </section>

  );
}

function select(state) {
  return {
    lessons: state.grammarActivities,
    fillInBlank: state.fillInBlank,
    concepts: state.concepts,
    fillInBlankConceptsFeedback: state.fillInBlankConceptsFeedback,
  };
}

export default connect(select)(FillInBlankActivities);
